import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import PageShell from "../components/PageShell";
import RadialGauge from "../components/RadialGauge";
import BandPill from "../components/BandPill";
import DimensionBars from "../components/DimensionBars";
import SourcesList from "../components/SourcesList";
import EarningsList from "../components/EarningsList";
import CTABlock from "../components/CTABlock";
import HeroBandCTA from "../components/HeroBandCTA";
import CollapsibleSection from "../components/CollapsibleSection";
import { getSharedReport } from "../lib/api";
import { splitRevenueRange } from "../lib/format";

export default function SharedReport() {
  const { shareId } = useParams();
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await getSharedReport(shareId);
        if (!cancelled) setReport(r);
      } catch (e) {
        if (!cancelled) setError("That report could not be found.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [shareId]);

  return (
    <PageShell>
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 sm:pt-8 pb-16">
        {error ? (
          <div className="gl-card text-center py-16" data-testid="shared-error">
            <h1 className="text-2xl text-white mb-2">Report not found</h1>
            <p className="text-[#b6c1b8] mb-6">{error}</p>
            <Link to="/" className="gl-cta inline-flex">Score a new idea</Link>
          </div>
        ) : !report ? (
          <div className="gl-card text-center py-16" data-testid="shared-loading">
            <div className="gl-pulse-dot mx-auto" />
            <p className="text-[#b6c1b8] mt-4">Loading report...</p>
          </div>
        ) : (
          <div className="space-y-5 sm:space-y-6" data-testid="shared-report">
            <div className="gl-card-lift gl-card-active p-4 sm:p-7">
              <div className="mb-4 sm:mb-5 text-center">
                <div className="gl-label mb-2">Shared Greenlight Score</div>
                <p className="text-white text-[15px] sm:text-lg max-w-2xl mx-auto leading-snug">
                  {report.idea_text}
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-8 items-center">
                <div className="flex flex-col items-center">
                  <div className="w-full max-w-[220px]">
                    <RadialGauge
                      score={report.final_score}
                      band={report.band}
                      size={200}
                      testId="overall-score"
                    />
                  </div>
                </div>
                <div className="flex flex-col">
                  <BandPill band={report.band} />
                </div>
              </div>
              {report.verdict_headline ? (
                <div className="mt-4 pt-4 sm:mt-5 sm:pt-5 border-t border-[#1a221c]">
                  <p className="text-white text-[15px] sm:text-base leading-relaxed">
                    {report.verdict_headline}
                  </p>
                </div>
              ) : null}
            </div>

            {/* Band-specific high-placement action button */}
            <HeroBandCTA band={report.band} offerUrl={report?.offer_url} />

            {/* Headline stat cards */}
            <SharedStatCards report={report} />

            <div>
              <h2 className="text-[22px] sm:text-2xl font-bold text-white tracking-tight mb-3 sm:mb-4">
                The Scorecard
              </h2>
              <DimensionBars
                dimensions={report.dimensions}
                notes={report.dimension_notes}
                biggestGaps={report.biggest_gap_dimensions || []}
              />
            </div>

            <CTABlock
              band={report.band}
              reframe={report?.gap_analysis?.suggested_reframe}
              projected={report?.gap_analysis?.projected_score}
              offerUrl={report?.offer_url}
            />

            <div>
              <h2 className="text-[22px] sm:text-2xl font-bold text-white mb-3 sm:mb-4 flex items-baseline gap-2">
                Who Is Already Making Money
                {(() => {
                  const n = (report.earnings_evidence || []).filter(
                    (e) => e && (e.reported_figure || "").trim() && (e.source_url || "").trim(),
                  ).length;
                  return n > 0 ? (
                    <span className="text-sm text-[#b6c1b8] font-medium tabular-nums">
                      ({n} found)
                    </span>
                  ) : null;
                })()}
              </h2>
              <EarningsList earnings={report.earnings_evidence} />
            </div>

            <CollapsibleSection
              title="The Sources We Used"
              count={(() => {
                const list = report.numbered_sources && report.numbered_sources.length
                  ? report.numbered_sources
                  : report.all_sources || [];
                return list.filter((s) => s && s.url).length;
              })()}
              testId="shared-sources-section"
              defaultOpen={false}
            >
              <SourcesList sources={report.numbered_sources && report.numbered_sources.length ? report.numbered_sources : report.all_sources} />
            </CollapsibleSection>

            <div className="text-center">
              <Link to="/" className="gl-cta inline-flex" data-testid="score-own-idea-cta">
                Score your own idea free
              </Link>
            </div>
          </div>
        )}
      </section>
    </PageShell>
  );
}

function SharedStatCards({ report }) {
  const rev = report.revenue_potential || "";
  const split = rev ? splitRevenueRange(rev) : null;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
      <div className="gl-card-lift p-4 sm:p-5">
        <div className="gl-label mb-2">Revenue Potential</div>
        {rev ? (
          split ? (
            <>
              <div className="text-[20px] sm:text-xl font-bold text-white leading-snug">
                Typical: {split.typical}
              </div>
              <div className="text-sm text-[#b6c8be] mt-2 font-semibold">
                Top operators: {split.top}
              </div>
            </>
          ) : (
            <div className="text-[20px] sm:text-xl font-bold text-white leading-snug">
              {rev}
            </div>
          )
        ) : (
          <div className="text-[15px] font-semibold text-[#b6c1b8] leading-snug">
            No same-model proof found in public sources.
          </div>
        )}
      </div>
      <div className="gl-card-lift p-4 sm:p-5">
        <div className="gl-label mb-2">Standout Proof</div>
        {report.proof_headline ? (
          <div className="text-[18px] sm:text-lg font-bold text-white leading-snug">
            {report.proof_headline}
          </div>
        ) : (
          <div className="text-[18px] sm:text-lg font-bold text-white leading-snug">
            No verified figure found
          </div>
        )}
      </div>
    </div>
  );
}
